import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  FaHome,
  FaChartLine,
  FaSearch,
  FaExclamationTriangle,
  FaArrowLeft,
  FaGlobe,
  FaBullhorn,
  FaCog,
  FaRocket,
  FaLightbulb, FaRupeeSign,
  FaHandPointRight
} from 'react-icons/fa';

const NotFound = () => {
  return (
    <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
            {/* Stock Chart Lines */}
            <div className="absolute top-10 left-0 w-64 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent"></div>
            <div className="absolute top-40 right-10 w-80 h-px bg-gradient-to-r from-transparent via-green-300/30 to-transparent rotate-45"></div>
            <div className="absolute bottom-20 left-20 w-96 h-px bg-gradient-to-r from-transparent via-purple-300/30 to-transparent rotate-12"></div>
            
            {/* Floating Stock Cards */}
            <motion.div
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/4 w-20 h-20 bg-gradient-to-br from-blue-100/30 to-blue-200/30 backdrop-blur-sm rounded-xl border border-blue-200/50 shadow-lg flex items-center justify-center"
            >
            <FaChartLine className="text-blue-500 text-2xl" />
            </motion.div>

            <motion.div
            animate={{ y: [0, 30, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute bottom-1/3 right-1/6 w-16 h-16 bg-gradient-to-br from-green-100/30 to-green-200/30 backdrop-blur-sm rounded-xl border border-green-200/50 shadow-lg flex items-center justify-center"
            >
            <FaRupeeSign className="text-green-500 text-xl" />
            </motion.div>

           <motion.div
            animate={{ y: [0, 30, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute bottom-1/3 left-1/6 w-16 h-16 bg-gradient-to-br from-yellow-100/30 to-orange-200/30 backdrop-blur-sm rounded-xl border border-yellow-200/50 shadow-lg flex items-center justify-center"
            >
            <FaChartLine className="text-yellow-500 text-xl" />
            </motion.div>

            <motion.div
            animate={{ x: [0, 40, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute top-1/3 right-1/3 w-24 h-24 bg-gradient-to-br from-purple-100/30 to-purple-200/30 backdrop-blur-sm rounded-xl border border-purple-200/50 shadow-lg flex items-center justify-center"
            >
            <FaRocket className="text-purple-500 text-3xl" />
            </motion.div>
            
            {/* Stock Market Grid Pattern */}
            <div className="absolute inset-0 opacity-10">
            <div className="h-full w-full" style={{
                backgroundImage: `linear-gradient(to right, #3b82f6 1px, transparent 1px),
                                linear-gradient(to bottom, #3b82f6 1px, transparent 1px)`,
                backgroundSize: '40px 40px'
            }}></div>
            </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
            <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
            >
            {/* Animated 404 */}
            <div className="relative mb-8">
                <motion.div
                animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 2, 0, -2, 0]
                }}
                transition={{ 
                    duration: 3, 
                    repeat: Infinity,
                    ease: "easeInOut" 
                }}
                className="text-9xl font-extrabold bg-gradient-to-r from-blue-600 via-blue-600 to-blue-600 bg-clip-text text-transparent"
                >
                404
                </motion.div>
                
                {/* Warning Icon */}
                <motion.div
                animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, -10, 0]
                }}
                transition={{ 
                    duration: 2, 
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                }}
                className="absolute -top-4 -right-4"
                >
                <FaExclamationTriangle className="text-4xl text-amber-500 drop-shadow-lg" />
                </motion.div>
            </div>
            
            {/* Stock Market Styled Title */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mb-6"
            >
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
                Oops! This page seems lost in space.
                </h1>
                <div className="h-1 w-32 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
            </motion.div>
            
            {/* Stock Market Themed Message */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="max-w-2xl mx-auto mb-8"
            >
                <p className="text-xl text-gray-700 mb-4">
                Looks like this stock page has been <span className="font-semibold text-red-500">delisted</span> or is temporarily <span className="font-semibold text-amber-500">unavailable</span>.
                </p>
                <p className="text-gray-600">
                The investment opportunity you're looking for might have moved or the market data is currently updating.
                </p>
            </motion.div>
            </motion.div>
            {/* Navigation Links */}
            <div className="relative z-10 flex flex-wrap justify-center gap-6 mb-12 px-4">
                <Link to="/" className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md transition">  
                <FaHome className="mr-2" /> Home
                </Link>
                <Link to="/portfolio/heat-map-dashboard" className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-md transition">
                <FaChartLine className="mr-2" /> Heat Map
                </Link>
                <Link to="/portfolio/registered-ra-platform" className="flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg shadow-md transition">
                <FaGlobe className="mr-2" /> RA Platform
                </Link>
                <Link to="/portfolio/intraday-calls-dashboard" className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-md transition">
                <FaRocket className="mr-2" /> Intraday Calls
                </Link>
                <Link to="/portfolio/fundamental-screener" className="flex items-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg shadow-md transition">
                <FaLightbulb className="mr-2" /> Fundamental Screener
                </Link>
                <Link to="/portfolio/return-screener" className="flex items-center px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg shadow-md transition">
                <FaHandPointRight className="mr-2" /> Return Screener
                </Link> 
            </div>
            
        </div>
        
        </div>
        <Footer />
    </>
  );
};

export default NotFound;